import React, { useState } from "react";
import { FastField, useFormikContext } from "formik";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Box from "@mui/material/Box";
import SvgIcon from "@mui/material/SvgIcon";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import ScanditScanner from "../barcode/ScanditScanner/ScanditScanner";
import useAuth from "../../hooks/useAuth";

const styles = {
  scanMenuItem: {
    border: 1,
    borderColor: "primary.main",
    borderRadius: 1,
  },
};

const ScannerSelect = ({ options = [], label, InputLabelProps, size, required, ...props }) => {
  const { user } = useAuth();
  const { setFieldValue } = useFormikContext();
  const [openScan, setOpenScan] = useState(false);

  const handleToggleScan = () => {
    setOpenScan(v => !v);
  };

  const handleDetectedCode = (code, fieldName) => {
    const matchingOption = options.find(([value, label]) => value === code || label === code);

    if (matchingOption) {
      setFieldValue(fieldName, matchingOption[0]);
    } else {
      const caseInsensitiveMatch = options.find(
        ([value, label]) =>
          String(value || "").toLowerCase() === String(code || "").toLowerCase() ||
          String(label || "").toLowerCase() === String(code || "").toLowerCase()
      );

      if (caseInsensitiveMatch) {
        setFieldValue(fieldName, caseInsensitiveMatch[0]);
      } else {
        setFieldValue(fieldName, code);
      }
    }

    props.onDetected && props.onDetected(code);
  };

  return (
    <Box position="relative">
      <FormControl fullWidth variant="outlined" size={size} required={required}>
        <FastField {...props}>
          {({ field, form, meta }) => {
            const error = (meta.touched || form.submitCount > 0) && meta.error;

            const handleSelectChange = event => {
              const value = event.target.value;
              if (value === "__SCAN_BARCODE__") {
                handleToggleScan();
              } else {
                form.setFieldValue(field.name, value);
              }
            };

            return (
              <>
                <InputLabel id={`select-label-${field.name}`} error={!!error}>{label}</InputLabel>
                <Select
                  {...props}
                  data-id={field.name}
                  labelId={`select-label-${field.name}`}
                  label={label}
                  id={field.name}
                  value={field.value || ""}
                  error={!!error}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                  onChange={handleSelectChange}
                >
                  {!props.disabled && (
                    <MenuItem
                      value="__SCAN_BARCODE__"
                      data-id="scan-barcode-item"
                      sx={styles.scanMenuItem}
                    >
                      <Box display="flex" alignItems="center" color="primary.main">
                        <SvgIcon fontSize="small" style={{ marginRight: 8 }}>
                          <QrCodeScannerIcon color="primary" />
                        </SvgIcon>
                        Scan a Barcode
                      </Box>
                    </MenuItem>
                  )}
                  {options.map(([value, label]) => (
                    <MenuItem key={value} value={value} data-id="select-item">
                      {label}
                    </MenuItem>
                  ))}
                </Select>

                {error && (
                  <FormHelperText htmlFor={field.name} error={!!error}>
                    {error}
                  </FormHelperText>
                )}
              </>
            );
          }}
        </FastField>
      </FormControl>

      {openScan && (
        <FastField name={props.name}>
          {({ field }) => (
            <ScanditScanner
              onDetected={code => handleDetectedCode(code, field.name)}
              onClose={handleToggleScan}
            />
          )}
        </FastField>
      )}
    </Box>
  );
};

export default ScannerSelect;
